import { ActionButton, TextArea } from '@react-spectrum/s2';
import Upload from '@react-spectrum/s2/icons/Upload';
import {
	memo,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { BlobImage } from '@/components/blob/BlobImage';
import { BridgewayGlyph } from '@/components/BridgewayGlyph';
import { FireflyIcon } from '@/components/icons/FireflyIcon';
import { useFireflyEditModal } from '@/features/fireflyImageEditor';
import { useOptimisticState } from '@/hooks/useOptimisticState';
import { UserSettingsContext } from '@/spaces/settings/UserSettingsContext';
import { cn } from '@/utils/cn.ts';

import type { InstagramPostContent } from '../../../../bridge/types/artifact.ts';

import { ImageEditingOverlay } from './ImageEditingOverlay';
import { PostDownloadButton } from './PostDownloadButton';
import { RailTooltip } from './RailTooltip';
import { SlideFilmstrip } from './SlideFilmstrip';
import { toolRailButtonClass } from './toolRail';
import type { ArtifactComponentProps } from './types';
import { useImageReplace } from './useImageReplace.tsx';
import { VibeEditButton } from './VibeEditButton';

interface PostReviewProps extends ArtifactComponentProps<InstagramPostContent> {
	artifactId: string;
	schedule?: { fire_at?: string } | null;
	businessUsername?: string;
}

// Nearest ancestor that actually scrolls vertically (the outer panel container).
const getScrollableAncestor = (el: HTMLElement | null): HTMLElement | null => {
	let node = el?.parentElement ?? null;
	while (node) {
		const overflowY = getComputedStyle(node).overflowY;
		if (overflowY === 'auto' || overflowY === 'scroll') return node;
		node = node.parentElement;
	}
	return null;
};

// Belt-and-braces for the residual scroll reset. Revealing the rail via CSS
// removed the re-render that used to trigger the snap on every hover, but Blink
// can still occasionally reset the panel's scroll container to the top a few
// frames after the pointer enters this `container-type` viewport
// (w3c/csswg-drafts#7875). `intendedTop` is the scroll position the reader was
// actually at (captured before the snap can land); for a short window we undo
// only the tell-tale collapse toward the top, and bail the instant the reader
// scrolls further down so we never fight them.
const guardAgainstScrollReset = (
	scroller: HTMLElement | null,
	intendedTop: number,
): void => {
	if (!scroller || intendedTop === 0) return;
	const deadline = performance.now() + 300;
	const tick = () => {
		const now = scroller.scrollTop;
		if (now > intendedTop) return;
		if (now < intendedTop) scroller.scrollTop = intendedTop;
		if (performance.now() < deadline) requestAnimationFrame(tick);
	};
	requestAnimationFrame(tick);
};

export const PostReview = memo(function PostReview({
	artifactId,
	content,
	schedule: _schedule,
	isReadOnly = false,
	onContentChange,
	businessUsername = 'yourbusiness',
}: PostReviewProps) {
	const settings = useContext(UserSettingsContext)?.settings;
	const bridgeApi = useBridgeApi();
	const [captionValue, setCaptionValue] = useOptimisticState(content.caption);
	const resolvedBusinessUsername =
		settings?.instagram_handle ?? businessUsername;
	const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
	const fireflyEdit = useFireflyEditModal();
	const viewportRef = useRef<HTMLDivElement>(null);
	// Pointer-inside is tracked in a ref, not state: the tool-rail reveals purely
	// via CSS :hover (group-hover), so entering the image causes NO React
	// re-render. That matters because this viewport is a `container-type`
	// size-containment context, and a re-render on pointer-enter forces a
	// containment relayout that makes Blink reset the panel's scroll container to
	// the top (w3c/csswg-drafts#7875) — the ADO-1309 scroll-snap bug. The ref
	// still lets the carousel arrow-key handler know when the pointer is over the
	// viewport without re-rendering.
	const isViewportHoveredRef = useRef(false);
	const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
	const hasPositionedRef = useRef(false);
	// Last scroll offset the reader settled on while NOT hovering the image — the
	// position the reset guard restores to. Kept current by a scroll listener so
	// the guard always knows the true pre-hover position even if the snap beats
	// the mouseenter read.
	const restScrollTopRef = useRef(0);
	const scrollerRef = useRef<HTMLElement | null>(null);

	const slides = content.image_urls ?? [];
	// A real-photos-only carousel is created with its slide plan (slide_prompts)
	// but NO generated images (image_urls empty). The owner fills it with their
	// own photos. The planned slot count comes straight from slide_prompts: while
	// the carousel is image-less, slide_prompts is never mutated (adding a photo
	// takes image_urls from 0 to >=1, which immediately hands over to the normal
	// filled view), so the live value is stable and correct here.
	const plannedSlotCount = content.slide_prompts?.length ?? 0;
	const isEmptyAwaitingUploads = slides.length === 0 && plannedSlotCount > 0;
	// "carousel" for layout/aspect purposes tracks the widest signal so an
	// image-less multi-slide plan still lays out as a carousel (4/5), matching
	// what it will look like once filled.
	const isCarousel = Math.max(slides.length, plannedSlotCount) > 1;
	const chatPanel = bridgeApi.user_state?.chat_panel;
	const selectedSlideIndex =
		chatPanel?.panel_type === 'artifact'
			? (chatPanel.selected_slide_index ?? 0)
			: 0;
	const safeSlide = Math.min(
		selectedSlideIndex,
		Math.max(slides.length - 1, 0),
	);

	const editingSlideIndices = new Set<number>();
	// A redraw is tracked apart from the set above. Its image_index counts over
	// the whole carousel the post asked for, while this set counts positions in
	// the deck the customer is looking at, so putting it in would mark a slide
	// that is not the one being redrawn. A redrawn slide has no tile to mark
	// anyway: its place in the deck does not exist yet.
	let hasOutstandingRedraw = false;
	for (const req of Object.values(
		bridgeApi.user_state?.outstanding_requests ?? {},
	)) {
		// Both an async vibe edit and an async manual upload capture an
		// image_index at request time and blind-write it on completion, so both
		// must block structural edits that would shift that index. A replace
		// without an explicit index targets slide 0.
		if (req?.type === 'request_edit_image' && req.artifact_id === artifactId) {
			editingSlideIndices.add(req.image_index);
		} else if (
			req?.type === 'request_replace_post_image' &&
			req.artifact_id === artifactId
		) {
			editingSlideIndices.add(req.image_index ?? 0);
		} else if (
			req?.type === 'request_redraw_slide' &&
			req.artifact_id === artifactId
		) {
			hasOutstandingRedraw = true;
		}
	}
	// A structural edit (add/remove/reorder) racing an in-flight vibe edit would
	// let the late edit blind-write a slide that has since moved, corrupting the
	// wrong one. Suspend all structural controls until every edit on this
	// artifact has landed. A redraw counts for the same reason: it puts a slide
	// back at a position worked out before it started, so an add, a delete or a
	// reorder in the meantime would move that position.
	const hasOutstandingEdit =
		editingSlideIndices.size > 0 || hasOutstandingRedraw;

	// Track the outer panel scroller and continuously remember the offset the
	// reader rests at while the pointer is away from the image. On hover-enter the
	// reset guard restores toward this remembered value, which is reliable even if
	// the snap lands before the enter handler can read the position.
	useEffect(() => {
		const scroller = getScrollableAncestor(viewportRef.current);
		scrollerRef.current = scroller;
		if (!scroller) return;
		restScrollTopRef.current = scroller.scrollTop;
		const onScroll = () => {
			if (!isViewportHoveredRef.current) {
				restScrollTopRef.current = scroller.scrollTop;
			}
		};
		scroller.addEventListener('scroll', onScroll, { passive: true });
		return () => scroller.removeEventListener('scroll', onScroll);
	}, []);

	const setSlide = useCallback(
		(i: number) => {
			bridgeApi.sendAction({
				type: 'do_set_chat_panel_slide',
				slide_index: i,
			});
		},
		[bridgeApi],
	);

	const goPrev = useCallback(
		() => setSlide(Math.max(0, safeSlide - 1)),
		[setSlide, safeSlide],
	);
	const goNext = useCallback(
		() => setSlide(Math.min(Math.max(slides.length - 1, 0), safeSlide + 1)),
		[setSlide, safeSlide, slides.length],
	);

	useEffect(() => {
		if (!isCarousel) return;
		const handler = (event: KeyboardEvent) => {
			// Only when the pointer is over the viewport — read the ref so the
			// pointer-enter itself never re-renders (see isViewportHoveredRef).
			if (!isViewportHoveredRef.current) return;
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				goPrev();
			} else if (event.key === 'ArrowRight') {
				event.preventDefault();
				goNext();
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [isCarousel, goPrev, goNext]);

	const goPrevRef = useRef(goPrev);
	const goNextRef = useRef(goNext);
	goPrevRef.current = goPrev;
	goNextRef.current = goNext;

	useEffect(() => {
		if (!isCarousel) return;
		const viewport = viewportRef.current;
		if (!viewport) return;
		const COOLDOWN_MS = 700;
		const MIN_DELTA = 4;
		let cooldownUntil = 0;
		const handler = (event: WheelEvent) => {
			if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
			event.preventDefault();
			if (Math.abs(event.deltaX) < MIN_DELTA) return;
			const now = performance.now();
			if (now < cooldownUntil) return;
			if (event.deltaX > 0) goNextRef.current();
			else goPrevRef.current();
			cooldownUntil = now + COOLDOWN_MS;
		};
		viewport.addEventListener('wheel', handler, { passive: false });
		return () => {
			viewport.removeEventListener('wheel', handler);
		};
	}, [isCarousel]);

	const handleCaptionCommit = useCallback(
		(value: string) => onContentChange?.({ caption: value }),
		[onContentChange],
	);

	const { openPicker, elements: imageReplaceElements } = useImageReplace({
		artifactId,
		slideIndex: isCarousel ? safeSlide : undefined,
		aspect: isCarousel ? '4:5' : '1:1',
		// A redraw can splice a restored slide in while the file dialog is open,
		// which shifts every later slide under an index nothing moved.
		slideUrls: slides,
	});

	// CSS aspect for a tile — matches the crop aspect above so an empty
	// placeholder occupies exactly the footprint its filled slide will.
	const cssAspectRatio = isCarousel ? '4 / 5' : '1 / 1';

	// Empty real-photos-only carousel: the owner picks ALL their photos at once
	// (allowMultiple) and each is appended as a slide via the insert path
	// (do_add_slide). Uploading N photos takes image_urls from 0 to N in one go,
	// so the panel lands directly in the normal FILLED carousel view — which
	// already has per-slide remove (X) and add (+) in the filmstrip — instead of
	// the collapse-prone one-at-a-time path.
	const {
		openPicker: openEmptyStatePicker,
		isUploading: isUploadingEmptyState,
		elements: emptyStateUploadElements,
	} = useImageReplace({
		artifactId,
		aspect: isCarousel ? '4:5' : '1:1',
		mode: 'insert',
		allowMultiple: true,
	});

	const centerActiveSlide = useCallback(
		(behavior: ScrollBehavior) => {
			const viewport = viewportRef.current;
			const slide = slideRefs.current[safeSlide];
			if (!viewport || !slide) return;
			const target =
				slide.offsetLeft - (viewport.clientWidth - slide.clientWidth) / 2;
			viewport.scrollTo({
				left: Math.max(0, target),
				behavior,
			});
		},
		[safeSlide],
	);

	useLayoutEffect(() => {
		centerActiveSlide(hasPositionedRef.current ? 'smooth' : 'auto');
		hasPositionedRef.current = true;
	}, [centerActiveSlide]);

	useEffect(() => {
		const slide = slideRefs.current[safeSlide];
		if (!slide) return;
		let firstCall = true;
		const observer = new ResizeObserver(() => {
			if (firstCall) {
				firstCall = false;
				return;
			}
			centerActiveSlide('auto');
		});
		observer.observe(slide);
		return () => observer.disconnect();
	}, [safeSlide, centerActiveSlide]);

	return (
		<div className="flex w-full justify-center">
			<div className="flex w-full flex-col">
				{isEmptyAwaitingUploads && !isReadOnly && (
					// Real-photos-only carousel: no images were generated. Show one large
					// CENTERED preview matching the normal carousel's big-preview footprint
					// — a dashed dropzone that opens a MULTI-file picker. Selecting all the
					// photos at once appends them as slides (image_urls 0 -> N), so the
					// panel lands directly in the normal FILLED carousel view, which has the
					// filmstrip with per-slide remove/add. The N-slot plan lives in
					// slide_prompts; the caption below shows how many to add.
					<div className="flex w-full flex-col">
						<div
							className="[container-type:inline-size] relative flex w-full
								items-center justify-center"
							style={{ height: 'min(62vh, 620px)' }}
						>
							{isUploadingEmptyState ? (
								// Uploading takes real time (crop + upload per file). Show the
								// same generating-style loading the panel uses elsewhere so the
								// wait is visible and never reads as broken.
								<div
									className="flex h-full animate-pulse items-center
										justify-center overflow-hidden rounded-2xl bg-gray-100"
									style={{ aspectRatio: cssAspectRatio }}
								>
									<BridgewayGlyph />
								</div>
							) : (
								<button
									type="button"
									onClick={openEmptyStatePicker}
									// Each photo picked here becomes its own slide, so this is a
									// structural change and it waits for the same reasons the
									// filmstrip does. An empty deck is exactly the deck a redraw
									// repairs, and a photo added while that redraw runs would
									// take the position the restored slide was aimed at.
									disabled={hasOutstandingEdit}
									aria-label="Add photos"
									className="flex h-full flex-shrink-0 cursor-pointer flex-col
										items-center justify-center gap-2 rounded-2xl border-2
										border-dashed border-gray-300 bg-gray-50 text-gray-500
										transition-colors hover:border-gray-400 hover:bg-gray-100
										hover:text-gray-700 disabled:cursor-not-allowed
										disabled:opacity-40"
									style={{ aspectRatio: cssAspectRatio }}
								>
									<Upload />
									<span className="text-sm font-medium">Add photos</span>
								</button>
							)}
						</div>
						<div className="px-4 py-3 text-center text-sm text-gray-600">
							{isUploadingEmptyState
								? 'Uploading your photos…'
								: isCarousel
									? `Real photos only — add your own ${plannedSlotCount} photos to fill this carousel.`
									: 'Real photos only — add your own photo for this post.'}
						</div>
						{emptyStateUploadElements}
					</div>
				)}
				{slides.length > 0 && (
					<div
						ref={viewportRef}
						className="group/viewport [container-type:inline-size] relative
							w-full overflow-x-hidden overscroll-x-contain scroll-smooth"
						style={{ height: 'min(62vh, 620px)' }}
						onMouseEnter={() => {
							isViewportHoveredRef.current = true;
							guardAgainstScrollReset(
								scrollerRef.current,
								restScrollTopRef.current,
							);
						}}
						onMouseLeave={() => {
							isViewportHoveredRef.current = false;
						}}
					>
						<div className="flex h-full items-center gap-3">
							<div
								aria-hidden
								className="h-full flex-shrink-0"
								style={{ width: '50cqw' }}
							/>
							{slides.map((url, i) => {
								const isActive = i === safeSlide;
								const isSlideEditing = editingSlideIndices.has(i);
								return (
									<div
										key={`${i}-${url}`}
										data-testid={`carousel-slide-${i}`}
										ref={(el) => {
											slideRefs.current[i] = el;
										}}
										onClick={isActive ? undefined : () => setSlide(i)}
										className={cn(
											`relative h-full flex-shrink-0 transition-opacity
											duration-300`,
											isActive
												? 'opacity-100'
												: 'cursor-pointer opacity-30 hover:opacity-50',
										)}
									>
										<BlobImage
											src={url}
											alt={
												isCarousel
													? `Post preview slide ${i + 1} of ${slides.length}`
													: 'Post preview'
											}
											onLoad={() => {
												if (isActive) centerActiveSlide('auto');
											}}
											className="block h-full w-auto max-w-[88cqw] rounded-2xl
												object-contain"
										/>

										{isActive && !isSlideEditing && (
											<div
												// Reveal on hover with pure CSS (group-hover) rather than
												// React state, so pointer-enter triggers no re-render and
												// no containment relayout — the root cause of the
												// ADO-1309 scroll-snap. The download menu portals outside
												// the viewport, un-hovering it; data-menu-open keeps the
												// rail visible while that menu is open.
												data-menu-open={isDownloadMenuOpen || undefined}
												className={cn(
													`pointer-events-none absolute top-4 left-4 z-10 flex
													flex-col gap-2 opacity-0 transition-opacity
													duration-150 group-hover/viewport:pointer-events-auto
													group-hover/viewport:opacity-100
													data-[menu-open]:pointer-events-auto
													data-[menu-open]:opacity-100`,
												)}
											>
												<RailTooltip label="Download">
													<PostDownloadButton
														imageUrls={slides}
														artifactId={artifactId}
														activeSlideIndex={safeSlide}
														onMenuOpenChange={setIsDownloadMenuOpen}
													/>
												</RailTooltip>
												{!isReadOnly && (
													<>
														<RailTooltip label="Upload Image">
															<ActionButton
																aria-label="Replace image"
																onPress={openPicker}
																UNSAFE_className={toolRailButtonClass}
															>
																<Upload />
															</ActionButton>
														</RailTooltip>
														<VibeEditButton />
														<RailTooltip label="Edit with Firefly">
															<ActionButton
																aria-label="Edit image"
																onPress={() =>
																	fireflyEdit.open(url, {
																		artifactId,
																		...(isCarousel && { imageIndex: i }),
																	})
																}
																UNSAFE_className={toolRailButtonClass}
															>
																<FireflyIcon />
															</ActionButton>
														</RailTooltip>
													</>
												)}
											</div>
										)}

										{isSlideEditing && (
											<div className="pointer-events-none absolute inset-0 z-20">
												<ImageEditingOverlay />
											</div>
										)}
									</div>
								);
							})}
							<div
								aria-hidden
								className="h-full flex-shrink-0"
								style={{ width: '50cqw' }}
							/>
						</div>

						{!isReadOnly && imageReplaceElements}
					</div>
				)}

				{/* Show the filmstrip whenever there is at least one real image (so the
				    owner can always add MORE slides or remove one — even a single-image
				    post can grow into a carousel), or a multi-slide plan is in flight.
				    A single standalone image with no carousel intent renders no strip. */}
				{(slides.length >= 1 || isCarousel) && (
					<SlideFilmstrip
						artifactId={artifactId}
						content={content}
						selectedIndex={safeSlide}
						isReadOnly={isReadOnly}
						disabled={hasOutstandingEdit}
					/>
				)}

				<div className="flex flex-col gap-4 px-4 py-3">
					<div className="flex flex-col gap-1">
						<span className="text-sm font-semibold text-[--text-primary]">
							{resolvedBusinessUsername}
						</span>

						{isReadOnly ? (
							<p
								className="text-sm leading-6 whitespace-pre-wrap
									text-[--text-secondary]"
							>
								{captionValue}
							</p>
						) : (
							<TextArea
								aria-label="Post caption"
								value={captionValue}
								onChange={(value) => {
									setCaptionValue(value);
								}}
								onBlur={() => {
									if (captionValue !== content.caption) {
										handleCaptionCommit(captionValue);
									}
								}}
							/>
						)}
					</div>
				</div>
			</div>
			{fireflyEdit.modal}
		</div>
	);
});
